import React, { useState, useEffect } from 'react';
import { Upload } from 'lucide-react';
import '../../style/productTags.css';
import { useSelector } from 'react-redux';

const TagForm = ({ tag, onSave, onCancel }) => {
  const { isGodownURL } = useSelector((state) => state.domain);
  const [formData, setFormData] = useState({ name: '', description: '', image: null });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [originalData, setOriginalData] = useState(null);

  const isEditing = !!tag;

  useEffect(() => {
    if (tag) {
      const initialData = {
        name: tag.name || '',
        description: tag.description || '',
        image: tag.image || null
      };
      setFormData(initialData);
      setOriginalData(initialData);
      setImagePreview(tag.image || null);
    }
  }, [tag]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
      setFormData(prev => ({ ...prev, image: file }));
      if (errors.image) {
        setErrors(prev => ({ ...prev, image: '' }));
      }
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
      setFormData(prev => ({ ...prev, image: file }));
      if (errors.image) {
        setErrors(prev => ({ ...prev, image: '' }));
      }
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Tag name is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      if (isEditing) {
        // For PATCH: only send changed fields
        const changedData = {};
        if (formData.name !== originalData.name) {
          changedData.name = formData.name.trim();
        }
        if (formData.description !== originalData.description) {
          changedData.description = formData.description.trim();
        }
        // Only include image if it's a new File object (user changed it)
        if (formData.image instanceof File) {
          changedData.image = formData.image;
        }
        await onSave(changedData);
      } else {
        // For POST: send all fields
        const cleanedData = {
          name: formData.name.trim(),
          description: formData.description.trim(),
        };
        // Only include image if provided
        if (formData.image) {
          cleanedData.image = formData.image;
        }
        await onSave(cleanedData);
      }
    } catch (error) {
      console.error('Error saving tag:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="tags-modal-overlay">
      <div className="tags-modal-container no-scrollbar">
        <div className="tags-modal-content">
          <div className="tags-modal-header">
            <h2 className="tags-modal-title">
              {isEditing ? 'Edit Tag' : 'Add New Tag'}
            </h2>
            <p className="tags-modal-subtitle">
              {isEditing ? 'Update tag information.' : 'Create a new product tag.'}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="tags-form">
            <div className="tags-modal-body">
              <div className="tags-form-group">
                <label htmlFor="name" className="tags-form-label">
                  Tag Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className={`tags-form-input ${errors.name ? 'error' : ''}`}
                  placeholder="Electronics"
                  required
                />
                {errors.name && (
                  <div className="tags-form-error">{errors.name}</div>
                )}
              </div>

              <div className="tags-form-group">
                <label htmlFor="image" className="tags-form-label">
                  Tag Image
                </label>
                <div
                  className={`tags-form-input ${errors.image ? 'error' : ''} ${imagePreview ? 'has-image' : ''}`}
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                  onClick={() => document.getElementById('image').click()}
                  style={{
                    border: errors.image ? '1px dashed #ef4444' : '1px dashed #d1d5db',
                    borderRadius: '8px',
                    padding: imagePreview ? '0' : '24px',
                    textAlign: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    position: 'relative',
                    minHeight: '120px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  {imagePreview ? (
                    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                      <img src={imagePreview} alt="Tag preview" style={{ width: '100%', height: '120px', objectFit: 'cover', borderRadius: '8px' }} />
                      <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'rgba(0, 0, 0, 0.5)',
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        opacity: 0,
                        transition: 'opacity 0.2s ease'
                      }}
                        onMouseEnter={(e) => e.currentTarget.style.opacity = 1}
                        onMouseLeave={(e) => e.currentTarget.style.opacity = 0}
                      >
                        <button
                          type="button"
                          style={{
                            padding: '8px 16px',
                            background: '#ffffff',
                            border: 'none',
                            borderRadius: '6px',
                            color: '#374151',
                            fontSize: '14px',
                            fontWeight: '500',
                            cursor: 'pointer'
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            document.getElementById('image').click();
                          }}
                        >
                          Change Image
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div style={{ marginBottom: '8px' }}>
                        <Upload size={24} color={isGodownURL ? "var(--primary-color-godown)" : "#3b82f6"} />
                      </div>
                      <p style={{ fontSize: '14px', color: '#64748b', margin: 0 }}>
                        The image should be JPG, PNG, WEBP format
                      </p>
                    </>
                  )}
                  <input
                    type="file"
                    id="image"
                    name="image"
                    accept="image/*"
                    onChange={handleImageChange}
                    style={{ display: 'none' }}
                  />
                </div>
                {errors.image && (
                  <div className="tags-form-error">{errors.image}</div>
                )}
              </div>

              <div className="tags-form-group">
                <label htmlFor="description" className="tags-form-label">
                  Description
                </label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  className="tags-form-textarea"
                  rows="3"
                  placeholder="Tag description"
                />
              </div>
            </div>

            <div className="tags-modal-footer">
              <button
                type="button"
                className="tags-btn-cancel"
                onClick={onCancel}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="tags-btn-primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span className="tags-spinner" />
                    Saving...
                  </>
                ) : (
                  isEditing ? 'Update Tag' : 'Add Tag'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default TagForm;
